import type { ApproverDecision, TransitionRejection } from '@zevik/schemas';

/**
 * What the gate screen says when the gate refuses
 * (docs/DECISIONS.md ZD-125 §3).
 *
 * ## The sentence is the API's; the names are resolved here
 *
 * A refusal arrives as the guard's sentence and, where it is about
 * findings, their ids as a field. The sentence is shown as it came — this
 * module composes none (ZD-118 §3). What it adds is the one thing the API
 * cannot: a finding id names nothing a reviewer recognises, and this screen
 * holds every finding of the run with its title, so the ids are resolved to
 * titles and offered as links into the list.
 *
 * ## Resolution goes through the standing decision, and why
 *
 * A refused id is `decision.finding_id` — the finding a decision was *made
 * on* — and a decision follows its finding across runs by `finding_key`
 * (ZD-061 §3). So an id from a decision recorded on an earlier run's finding
 * is not among this run's ids, while the finding it is about is: the run's
 * finding with the same key. Resolving by key through the standing decision
 * finds it; resolving by id alone would call it unnamed. An id that resolves
 * through neither route names a finding this run does not produce, which is
 * what the orphaned-decision clause is about, and it is **counted rather than
 * printed** — an id in front of a person is the audience defect this screen
 * exists not to repeat, and the API's sentence already says what those are.
 *
 * ## This screen does not open a round
 *
 * The deferral refusal names deferred findings by title and stops there; the
 * round that would discharge them is `sds-12j`'s screen. The boundary is
 * *name what is deferred* here, *do something about it* there.
 */

export interface Named {
  finding_id: string;
  title: string;
}

export interface RefusalNames {
  named: Named[];
  /** Ids that resolve to no finding of this run, by count. */
  unnamed: number;
}

export function namesFor(
  rejection: TransitionRejection,
  findings: readonly { finding: { finding_id: string; title: string }; finding_key: string }[],
  standing: ReadonlyMap<string, ApproverDecision>,
): RefusalNames {
  if (rejection.code !== 'guard_failed') return { named: [], unnamed: 0 };
  const byId = new Map(findings.map((entry) => [entry.finding.finding_id, entry] as const));
  const byKey = new Map(findings.map((entry) => [entry.finding_key, entry] as const));
  const keyOfDecidedId = new Map<string, string>();
  for (const decision of standing.values()) {
    keyOfDecidedId.set(decision.finding_id, decision.finding_key);
  }

  const named: Named[] = [];
  let unnamed = 0;
  for (const id of rejection.finding_ids) {
    const key = keyOfDecidedId.get(id);
    const entry = byId.get(id) ?? (key === undefined ? undefined : byKey.get(key));
    if (entry === undefined) {
      unnamed += 1;
      continue;
    }
    named.push({ finding_id: entry.finding.finding_id, title: entry.finding.title });
  }
  return { named, unnamed };
}

/**
 * The refusal's count line, where any id went unnamed. Says only what the
 * count establishes — that these findings are not among this run's — and
 * leaves the reason to the API's sentence beside it.
 */
export function unnamedSentence(unnamed: number): string | null {
  if (unnamed === 0) return null;
  return unnamed === 1
    ? '1 of these is not among the findings this run produced.'
    : `${String(unnamed)} of these are not among the findings this run produced.`;
}
